/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/systemServices.scss';

import React, {MouseEventHandler, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import SystemService from '../../../models/systemServiceModel';

import {ChangeFunction, Loader} from '@dvrd/dvr-controls';

import {BluemateButton} from '../../controls';

import BluemateSwitch from '../../controls/switch/bluemateSwitch';

interface Props {
    onToggleService: (service: SystemService) => ChangeFunction;
    onToggleAll: MouseEventHandler;
    services: Array<SystemService>;
    loading: boolean;
    someActive: boolean;
}

export default function SystemServices(props: Props) {
    const {t} = useTranslation();
    const {someActive, services, onToggleService, onToggleAll, loading} = props;
    const toggleLabel = useMemo(() => {
        if (someActive) return t('admin.systemService.disableAll', 'Alles uitschakelen');
        return t('admin.systemService.enableAll', 'Alles inschakelen');
    }, [someActive, t]);

    function onClickBack() {
        history.back();
    }

    function getServiceAction(service: SystemService): string {
        const action = service.active
            ? t('admin.systemService.enabled', 'ingeschakeld')
            : t('admin.systemService.disabled', 'uitgeschakeld');
        const format = 'DD-MM-YYYY HH:mm';
        const date = service.updatedAtMoment?.format(format) ?? service.createdAtMoment.format(format);
        return `(${action} ${t('admin.systemService.on', 'op')} ${date})`;
    }

    function renderService(service: SystemService) {
        return (
            <div key={service.id} className='service'>
                <BluemateSwitch value={service.active} onChange={onToggleService(service)} disabled={loading} />
                <label>{service.translatedName}</label>
                <label className='updated'>{getServiceAction(service)}</label>
            </div>
        );
    }

    return (
        <div className='system-services'>
            <div className='btns-container'>
                <BluemateButton
                    onClick={onClickBack}
                    label={t('admin.systemService.back', 'Terug')}
                    disabled={loading}
                />
                <BluemateButton onClick={onToggleAll} label={toggleLabel} disabled={loading} secondary />
            </div>
            <div className='services-container'>
                <Loader active={loading} />
                {services.map(renderService)}
            </div>
        </div>
    );
}
